import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { trpc } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	addLineItemSchema,
	formatDollars,
	fromCents,
	toCents,
	updateLineItemSchema,
} from "@payrollmart-invoice/shared";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

interface LineItem {
	id: string;
	description: string;
	quantity: number;
	unitAmount: number;
	amount: number;
}

interface Props {
	invoiceId: string;
	lineItems: LineItem[];
	totalAmount: number;
	/** When true the table renders read-only (no Edit/Delete/Add affordances).
	 *  Set by the parent based on `invoice.qboInvoiceId !== null || status === "pushed"`. */
	isLocked: boolean;
	/** Compact: tighter padding for the review queue layout. */
	compact?: boolean;
	/** Caller invalidates parent query (e.g. `utils.invoices.getById.invalidate()`). */
	onMutated: () => void | Promise<void>;
}

type DialogState = { mode: "closed" } | { mode: "add" } | { mode: "edit"; lineItem: LineItem };

export function LineItemsTable({
	invoiceId,
	lineItems,
	totalAmount,
	isLocked,
	compact,
	onMutated,
}: Props) {
	const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
	const [confirmDelete, setConfirmDelete] = useState<LineItem | null>(null);

	const removeMutation = trpc.invoices.removeLineItem.useMutation();

	async function handleConfirmDelete() {
		if (!confirmDelete) return;
		try {
			await removeMutation.mutateAsync({ lineItemId: confirmDelete.id });
			await onMutated();
			toast.success("Line item removed");
			setConfirmDelete(null);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to remove line item");
		}
	}

	const cellPad = compact ? "px-4 py-2" : "px-6 py-3";
	const headPad = compact ? "px-4 py-2" : "px-6 py-2.5";

	return (
		<div className="overflow-hidden rounded-lg border border-[#e5e7eb] bg-white">
			<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
				<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">Line items</h2>
				{!isLocked && (
					<button
						type="button"
						onClick={() => setDialog({ mode: "add" })}
						className="inline-flex h-8 items-center gap-1.5 rounded border border-[#e5e7eb] bg-white px-3 text-xs font-medium text-[#1e1e20] transition-colors hover:bg-[#f8f9fb] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30"
					>
						<Plus className="h-3.5 w-3.5" strokeWidth={2} />
						Add line
					</button>
				)}
			</div>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-[#f3f4f6] bg-[#f8f9fb]">
						<th
							className={`${headPad} text-left text-xs font-medium uppercase tracking-wide text-[#6b7280]`}
						>
							Description
						</th>
						<th
							className={`${headPad} text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]`}
						>
							Qty
						</th>
						<th
							className={`${headPad} text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]`}
						>
							Unit Price
						</th>
						<th
							className={`${headPad} text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]`}
						>
							Amount
						</th>
						{!isLocked && <th className={`${headPad} w-24`} aria-label="actions" />}
					</tr>
				</thead>
				<tbody className="divide-y divide-[#f3f4f6]">
					{lineItems.length === 0 ? (
						<tr>
							<td
								colSpan={isLocked ? 4 : 5}
								className={`${cellPad} text-center text-sm text-[#9ca3af]`}
							>
								No line items yet.
								{!isLocked && (
									<button
										type="button"
										onClick={() => setDialog({ mode: "add" })}
										className="ml-2 text-[#4f46e5] hover:underline"
									>
										Add the first one.
									</button>
								)}
							</td>
						</tr>
					) : (
						lineItems.map((item) => (
							<tr key={item.id}>
								<td className={`${cellPad} text-[#1e1e20]`}>{item.description}</td>
								<td className={`${cellPad} text-right tabular-nums text-[#6b7280]`}>
									{item.quantity}
								</td>
								<td className={`${cellPad} text-right tabular-nums text-[#6b7280]`}>
									{formatDollars(item.unitAmount)}
								</td>
								<td className={`${cellPad} text-right tabular-nums text-[#1e1e20]`}>
									{formatDollars(item.amount)}
								</td>
								{!isLocked && (
									<td className={`${cellPad} text-right`}>
										<div className="inline-flex items-center gap-1">
											<button
												type="button"
												onClick={() => setDialog({ mode: "edit", lineItem: item })}
												aria-label={`Edit ${item.description}`}
												className="rounded p-1 text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#1e1e20] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30"
											>
												<Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
											</button>
											<button
												type="button"
												onClick={() => setConfirmDelete(item)}
												aria-label={`Remove ${item.description}`}
												className="rounded p-1 text-[#6b7280] hover:bg-[#fef2f2] hover:text-[#dc2626] focus:outline-none focus:ring-2 focus:ring-[#dc2626]/30"
											>
												<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
											</button>
										</div>
									</td>
								)}
							</tr>
						))
					)}
				</tbody>
				<tfoot>
					<tr className="border-t-2 border-[#e5e7eb] bg-[#f8f9fb]">
						{/* "Total" label spans the first three columns (Description, Qty,
						    Unit Price) regardless of lock state; the next cell is the
						    total amount in the Amount column; an empty trailing <td>
						    keeps the Actions column aligned when unlocked. */}
						<td colSpan={3} className={`${cellPad} font-semibold text-[#1e1e20]`}>
							Total
						</td>
						<td className={`${cellPad} text-right tabular-nums font-semibold text-[#1e1e20]`}>
							{formatDollars(totalAmount)}
						</td>
						{!isLocked && <td className={cellPad} aria-hidden="true" />}
					</tr>
				</tfoot>
			</table>

			<LineItemFormDialog
				state={dialog}
				invoiceId={invoiceId}
				onClose={() => setDialog({ mode: "closed" })}
				onSaved={onMutated}
			/>

			<ConfirmDialog
				open={confirmDelete !== null}
				onOpenChange={(open) => {
					if (!open) setConfirmDelete(null);
				}}
				title="Remove line item?"
				description={
					confirmDelete
						? `"${confirmDelete.description}" (${formatDollars(confirmDelete.amount)}) will be removed from this invoice. The invoice total will be recomputed.`
						: ""
				}
				confirmLabel={removeMutation.isPending ? "Removing…" : "Remove"}
				cancelLabel="Cancel"
				destructive
				isPending={removeMutation.isPending}
				onConfirm={handleConfirmDelete}
			/>
		</div>
	);
}

// The dialog form uses a single shape for both add + edit. Cents are stored as
// integers; the input displays the dollar string and we convert at the boundary.
const formSchema = z.object({
	description: z.string().min(1, "Required").max(255),
	quantity: z.coerce.number().int().positive(),
	unitAmount: z.string(),
	amount: z.string(),
});
type FormValues = z.infer<typeof formSchema>;

interface DialogProps {
	state: DialogState;
	invoiceId: string;
	onClose: () => void;
	onSaved: () => void | Promise<void>;
}

function LineItemFormDialog({ state, invoiceId, onClose, onSaved }: DialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const addMutation = trpc.invoices.addLineItem.useMutation();
	const updateMutation = trpc.invoices.updateLineItem.useMutation();
	const isOpen = state.mode !== "closed";
	const isEdit = state.mode === "edit";

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: { description: "", quantity: 1, unitAmount: "0.00", amount: "0.00" },
	});

	useEffect(() => {
		if (state.mode === "edit") {
			reset({
				description: state.lineItem.description,
				quantity: state.lineItem.quantity,
				unitAmount: fromCents(state.lineItem.unitAmount).toFixed(2),
				amount: fromCents(state.lineItem.amount).toFixed(2),
			});
		} else if (state.mode === "add") {
			reset({ description: "", quantity: 1, unitAmount: "0.00", amount: "0.00" });
		}
	}, [state, reset]);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;
		if (isOpen && !dialog.open) dialog.showModal();
		else if (!isOpen && dialog.open) dialog.close();
	}, [isOpen]);

	// Backdrop click → close. Attached via addEventListener rather than JSX
	// `onClick` so we don't trip Biome's `useKeyWithClickEvents` rule — keyboard
	// users already get ESC for free from the native <dialog>. Same pattern
	// ConfirmDialog uses (see its inline comment).
	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;
		const handler = (e: MouseEvent) => {
			if (e.target === dialog) onClose();
		};
		dialog.addEventListener("click", handler);
		return () => dialog.removeEventListener("click", handler);
	}, [onClose]);

	async function onValid(values: FormValues) {
		// Parse with the project-canonical toCents — handles both string + number
		// inputs and stays in integer cents (named invariant: never floating-point).
		const unitAmount = toCents(values.unitAmount);
		const amount = toCents(values.amount);
		if (unitAmount < 0 || amount < 0) {
			toast.error("Amounts cannot be negative");
			return;
		}

		try {
			if (state.mode === "edit") {
				await updateMutation.mutateAsync({
					lineItemId: state.lineItem.id,
					description: values.description,
					quantity: values.quantity,
					unitAmount,
					amount,
				});
				toast.success("Line item updated");
			} else if (state.mode === "add") {
				await addMutation.mutateAsync({
					invoiceId,
					description: values.description,
					quantity: values.quantity,
					unitAmount,
					amount,
				});
				toast.success("Line item added");
			}
			await onSaved();
			onClose();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to save line item");
		}
	}

	return (
		<dialog
			ref={dialogRef}
			onClose={onClose}
			className="w-full max-w-md rounded-lg p-0 backdrop:bg-[#1e1e20]/40"
		>
			<form
				onSubmit={handleSubmit(onValid)}
				className="space-y-4 bg-white p-6"
				aria-labelledby="line-item-dialog-title"
			>
				<h2 id="line-item-dialog-title" className="text-base font-semibold text-[#1e1e20]">
					{isEdit ? "Edit line item" : "Add line item"}
				</h2>

				<Field label="Description" error={errors.description?.message}>
					<input
						type="text"
						{...register("description")}
						className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					/>
				</Field>

				<div className="grid grid-cols-3 gap-3">
					<Field label="Qty" error={errors.quantity?.message}>
						<input
							type="number"
							step="1"
							min="1"
							{...register("quantity")}
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm tabular-nums text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						/>
					</Field>
					<Field label="Unit price ($)" error={errors.unitAmount?.message}>
						<input
							type="text"
							inputMode="decimal"
							{...register("unitAmount")}
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm tabular-nums text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						/>
					</Field>
					<Field label="Amount ($)" error={errors.amount?.message}>
						<input
							type="text"
							inputMode="decimal"
							{...register("amount")}
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm tabular-nums text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						/>
					</Field>
				</div>

				<p className="text-xs text-[#9ca3af]">
					Amount is independent of qty × unit price — ad-hoc adjustments (discounts, rounding
					settlements) can legitimately diverge.
				</p>

				<div className="flex justify-end gap-2 pt-2">
					<button
						type="button"
						onClick={onClose}
						className="h-9 rounded border border-[#e5e7eb] bg-white px-4 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f8f9fb]"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={isSubmitting}
						className="h-9 rounded bg-[#4f46e5] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#4338ca] disabled:opacity-50"
					>
						{isSubmitting ? "Saving…" : isEdit ? "Save changes" : "Add line"}
					</button>
				</div>
			</form>
		</dialog>
	);
}

function Field({
	label,
	error,
	children,
}: {
	label: string;
	error?: string;
	children: ReactNode;
}) {
	// Biome's `noLabelWithoutControl` can't see through the children prop, so
	// we use a div wrapper instead of <label>. The visual styling is identical;
	// trade-off is no click-on-label-to-focus-input, which we'd want to restore
	// later with a name+htmlFor refactor if usability complaints land.
	return (
		<div className="space-y-1 block">
			<span className="text-xs font-medium text-[#1e1e20]">{label}</span>
			{children}
			{error && <span className="block text-xs text-[#dc2626]">{error}</span>}
		</div>
	);
}

// Silence unused-import warnings — kept so future strict-mode validation can
// reuse the canonical Zod schemas without re-importing.
export const _schemaSentinel = { updateLineItemSchema, addLineItemSchema };
